import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { SessionUser } from '@salonomy/types';
import { Button, Field, Modal, Pill, TextInput } from '@salonomy/ui';
import { useAddPrivatePractice } from '../../data/practitioner.js';
import { ApiClientError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';
import './practice.css';

/**
 * WS11 — the "start your own practice" guided modal (brief 11 §1; ADR-0013 §4). An ALREADY-authenticated
 * practitioner (typically a chair_renter with residencies) adds their OWN one-chair studio to the account
 * they already use — one login across their whole working life (brief 11 non-negotiable #7). It mirrors
 * the onboarding wizard's vocabulary: a practice name (their solo studio's salon name) + a public booking
 * slug. No second tenancy model is introduced — "solo studio" is a salon whose owner is its only chair.
 *
 * The studio is a NEW tenant the server creates and switches the session into; on success we push that
 * switch through the public auth API exactly as the salon switcher does (auth.switchSalon — sets the
 * principal + clears the cache for the new tenant). We never mint credentials here (the brand-new-account
 * pre-session signup path is a separate marketing/WS3 flow on the API).
 *
 * HONESTY (ADR-0013): the billing line is shown as a PROPOSAL — the Solo plan covers their own tenant, and
 * "you're never billed twice for the same chair" is stated plainly. No price is computed on the client; the
 * £15 figure is the published seed default and is labelled a proposal where it appears.
 */
export function StartPracticeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { switchSalon } = useAuth();
  const add = useAddPrivatePractice();

  const [practiceName, setPracticeName] = useState('');
  const [slug, setSlug] = useState('');
  // Manual slug edits stop us auto-deriving it from the name; before that, the slug tracks the name.
  const [slugTouched, setSlugTouched] = useState(false);
  // After the studio is created we keep its principal so the closing step can switch into it.
  const [created, setCreated] = useState<SessionUser | null>(null);
  const [switching, setSwitching] = useState(false);

  // Derive a slug suggestion from the practice name (DISPLAY-only; the server is the authority and
  // re-validates the slug — a clash returns 409, surfaced below). Matches the public Slug shape.
  const derivedSlug = useMemo(() => suggestSlug(practiceName), [practiceName]);
  const effectiveSlug = (slugTouched ? slug : derivedSlug).trim();

  const nameOk = practiceName.trim().length > 0;
  const slugOk = SLUG_RE.test(effectiveSlug);
  const canSubmit = nameOk && slugOk && !add.isPending;

  function reset() {
    setPracticeName('');
    setSlug('');
    setSlugTouched(false);
    setCreated(null);
    setSwitching(false);
  }

  function close() {
    if (add.isPending || switching) return;
    reset();
    onClose();
  }

  async function submit() {
    if (!canSubmit) return;
    try {
      const me = await add.mutateAsync({
        practiceName: practiceName.trim(),
        slug: effectiveSlug,
      });
      setCreated(me);
      toast.success('Your studio is ready');
    } catch (err) {
      // A duplicate slug (or name) is the common case — give the practitioner the exact reason.
      if (err instanceof ApiClientError && err.isConflict) {
        toast.error('That booking link is already taken — try another.');
      } else {
        toast.error(err instanceof Error ? err.message : 'Could not create your studio.');
      }
    }
  }

  // Switch the session into the new studio tenant via the public auth API (sets the principal + clears
  // the cache, exactly like the salon switcher), then land on the diary of the new studio.
  async function switchIn() {
    if (!created) return;
    setSwitching(true);
    try {
      await switchSalon(created.salonId);
      reset();
      onClose();
      void navigate({ to: '/' });
    } catch {
      toast.error('Created your studio, but couldn’t switch into it — use the salon switcher.');
      setSwitching(false);
    }
  }

  if (created) {
    return (
      <Modal open={open} onClose={close} title="Your studio is ready" width={480}>
        <div className="practice-done">
          <div className="practice-done__title">
            {created.displayName ? `${created.displayName}’s studio is set up.` : 'Studio set up.'}
          </div>
          <div className="practice-done__body">
            Your solo studio is now a salon on your account — its own diary, clients, availability,
            payments and booking page. You can switch into it now, or carry on where you are and
            switch from the salon menu any time. One login, every place you work.
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" onClick={close} disabled={switching}>
            Stay here
          </Button>
          <Button onClick={switchIn} disabled={switching}>
            {switching ? 'Switching…' : 'Switch into my studio'}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={close} title="Start your own practice" width={480}>
      <div className="muted" style={{ fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
        Open your own one-chair studio on the same account you already use. You keep one login
        across every salon you work in and your own studio in between — one diary for your whole
        week.
      </div>

      <Field label="Practice name" hint="The name clients see for your own studio.">
        <TextInput
          autoFocus
          value={practiceName}
          onChange={(e) => setPracticeName(e.target.value)}
          placeholder="e.g. Alex Rivera Hair"
        />
      </Field>

      <Field
        label="Booking link"
        hint="Lowercase letters, numbers and hyphens — your personal booking page."
        error={effectiveSlug && !slugOk ? 'Lowercase letters, numbers and hyphens only.' : null}
      >
        <TextInput
          value={slugTouched ? slug : derivedSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="alex-rivera"
        />
      </Field>

      <div className="practice-slug">
        Clients will book with you at <strong>book.salonomy.com/{effectiveSlug || '…'}</strong>
      </div>

      {/* Billing — stated plainly as a PROPOSAL (ADR-0013). No figure is computed on the client. */}
      <div className="practice-billing">
        <div className="practice-billing__head">
          <span className="practice-billing__title">How it’s billed</span>
          <Pill tone="soft" className="mono">
            Proposal
          </Pill>
        </div>
        <div className="practice-billing__body">
          Your studio is billed on the <strong>Solo plan</strong> — a one-chair tenant — proposed at
          £15/month, no contract, one-click cancel. If you also rent a chair in a salon that’s on
          Salonomy, the salon pays for that chair:{' '}
          <strong>you’re never billed twice for the same chair</strong>. Prices are a proposal until
          confirmed; nothing is charged until you subscribe.
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="ghost" onClick={close} disabled={add.isPending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {add.isPending ? 'Creating…' : 'Create my studio'}
        </Button>
      </div>
    </Modal>
  );
}

// The public Slug shape (packages/types primitives.ts): 2–40 chars, lowercase letters/digits/hyphens,
// no leading/trailing hyphen. DISPLAY-only validation — the server re-validates and owns slug uniqueness.
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** Derive a slug suggestion from a name (lowercase, hyphenate, strip non-slug chars). Display only. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}
